import React, { useState } from "react";
import Modal from "./Modal";
import { toast } from "sonner";
import { FileSpreadsheet, Contact, Loader2, Download } from "lucide-react";
import api from "../lib/api";
import { downloadCSV, downloadAllVCards } from "../lib/vcard";

export default function ExportDialog({ open, onClose }) {
  const [busy, setBusy] = useState(null); // "csv" | "vcf" | null

  const run = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.get("/cards");
      if (!data || data.length === 0) {
        toast.error("No contacts to export yet");
        return;
      }
      if (kind === "csv") {
        downloadCSV(data);
        toast.success(`Exported ${data.length} contacts to CSV`);
      } else {
        downloadAllVCards(data);
        toast.success(`Exported ${data.length} contacts (.vcf)`);
      }
      onClose();
    } catch (e) {
      toast.error("Export failed. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const opt =
    "w-full flex items-start gap-4 p-4 rounded-xl bg-[#1E293B] border border-slate-700 hover:border-blue-500 hover:bg-slate-800 transition-colors text-left disabled:opacity-60";

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-md" testId="export-dialog">
      <div className="p-6">
        <p className="overline text-blue-500 mb-1">Take your network with you</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Export contacts</h2>
        <p className="text-sm text-slate-400 mb-6">
          Download your whole Rolodex — open it in a spreadsheet or import it straight into your phone.
        </p>

        <div className="space-y-3">
          <button data-testid="export-vcf-button" onClick={() => run("vcf")} disabled={!!busy} className={opt}>
            <div className="w-11 h-11 rounded-lg bg-blue-600/15 flex items-center justify-center shrink-0">
              {busy === "vcf" ? (
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
              ) : (
                <Contact className="w-5 h-5 text-blue-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Phone contacts (.vcf)</p>
              <p className="text-xs text-slate-400 mt-0.5">
                One tap to import everyone into your iPhone or Android contacts.
              </p>
            </div>
          </button>

          <button data-testid="export-csv-button" onClick={() => run("csv")} disabled={!!busy} className={opt}>
            <div className="w-11 h-11 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
              {busy === "csv" ? (
                <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Spreadsheet (.csv)</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Open in Excel, Google Sheets or Numbers with every field.
              </p>
            </div>
          </button>
        </div>

        <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 mt-5">
          <Download className="w-3.5 h-3.5" /> Exports include all contacts, not just the current filter.
        </p>
      </div>
    </Modal>
  );
}
